import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { startAIGeneration, startMermaidImport, importDiagramFromFile } from '../../lib/editorActions';
import { Upload, Sparkles, Plus, Search, ArrowUpDown, Star, Workflow, ChevronDown } from '@/components/icons';
import {
  useDiagramsQuery,
  useFoldersQuery,
  useDeleteDiagramMutation,
  useDuplicateDiagramMutation,
  useToggleStarMutation,
  useMoveDiagramMutation,
  usePatchDiagramMutation,
  useUpdateDiagramIconMutation,
} from '../../hooks/queries';
import { useAuth } from '../Auth/AuthProvider';
import { useTabs } from '../../context/TabsContext';
import { DiagramRowMenu } from '../../components/shell/DiagramRowMenu';
import { DiagramRowContextMenu } from '../../components/shell/DiagramRowContextMenu';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../components/ui/beui';
import { DiagramSettingsModal, DeleteConfirmModal } from '../../components/ui';
import ShareModal from '../../components/ui/ShareModal';
import { DiagramIconPicker } from '../../components/DiagramIconPicker';
import { TemplateLogoStack } from '../../components/TemplateLogoStack';
import TemplatePreviewDialog from '../../components/TemplatePreviewDialog';
import { getTemplateById, TEMPLATE_CATEGORIES, type Template } from '../../data/templates';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryClient';
import type { DiagramMetadata } from '../../types';

function relativeTime(iso: string): string {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} minutes ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hours ago`;
  if (s < 172800) return 'Yesterday';
  if (s < 604800) return `${Math.floor(s / 86400)} days ago`;
  const w = Math.floor(s / 604800);
  return `${w} week${w > 1 ? 's' : ''} ago`;
}

// The dashboard diagram icon is now an inline, editable DiagramIconPicker (see rows).

const FEATURED_TEMPLATES = [
  'basic-microservices',
  'aws-serverless',
  'event-driven-architecture',
  'mern-stack',
]
  .map((id) => getTemplateById(id))
  .filter((template): template is Template => Boolean(template));

function templateMeta(template: Template): string {
  const category = TEMPLATE_CATEGORIES.find((item) => item.id === template.category)?.label;
  return `${category ?? template.category} · ${template.nodes.length} nodes`;
}

const TILE_BUTTON =
  'ss-press group flex flex-col overflow-hidden rounded-xl border border-border bg-card text-left shadow-sm transition-colors hover:border-border/80 hover:bg-muted';
const TILE_PREVIEW =
  'relative flex h-[78px] items-center justify-center overflow-hidden border-b border-border';

export function StarterTiles() {
  const navigate = useNavigate();
  const [preview, setPreview] = useState<Template | null>(null);

  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between">
        <h2 className="text-[14.5px] font-semibold tracking-tight">Start a new diagram</h2>
        <Link to="/templates" className="ss-press text-[12.5px] font-semibold text-primary">
          Browse all templates →
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <button onClick={() => navigate('/editor')} className={TILE_BUTTON}>
          <div className={`${TILE_PREVIEW} bg-muted [background-image:radial-gradient(var(--border)_1px,transparent_1px)] [background-size:11px_11px]`}>
            <span className="grid h-8 w-8 place-items-center rounded-md border border-border bg-card text-muted-foreground shadow-sm">
              <Plus className="h-4 w-4" />
            </span>
          </div>
          <div className="px-3 pb-2.5 pt-2">
            <div className="text-[12.5px] font-semibold">Blank canvas</div>
            <div className="text-[11px] text-muted-foreground">Empty diagram</div>
          </div>
        </button>

        <button onClick={() => startAIGeneration(navigate)} className={TILE_BUTTON}>
          <div className={`${TILE_PREVIEW} bg-gradient-to-br from-primary/10 to-fuchsia-500/10`}>
            <span className="grid h-8 w-8 place-items-center rounded-md border border-primary/40 bg-card text-primary shadow-sm">
              <Sparkles className="h-4 w-4" />
            </span>
          </div>
          <div className="px-3 pb-2.5 pt-2">
            <div className="text-[12.5px] font-semibold">Generate with AI</div>
            <div className="text-[11px] text-muted-foreground">From a prompt</div>
          </div>
        </button>

        {FEATURED_TEMPLATES.map((template) => (
          <button key={template.id} onClick={() => setPreview(template)} className={TILE_BUTTON}>
            <div className={`${TILE_PREVIEW} bg-muted`}>
              <TemplateLogoStack template={template} />
            </div>
            <div className="px-3 pb-2.5 pt-2">
              <div className="truncate text-[12.5px] font-semibold">{template.name}</div>
              <div className="truncate text-[11px] text-muted-foreground">{templateMeta(template)}</div>
            </div>
          </button>
        ))}
      </div>
      <TemplatePreviewDialog template={preview} onClose={() => setPreview(null)} />
    </section>
  );
}

const idxTab = 'ss-press flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors';

export default function Home() {
  const { isAuthenticated } = useAuth();
  const { data: diagrams = [], isLoading } = useDiagramsQuery(isAuthenticated);
  const { data: folders = [] } = useFoldersQuery(isAuthenticated);
  const { openTab } = useTabs();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');

  const deleteMut = useDeleteDiagramMutation();
  const duplicateMut = useDuplicateDiagramMutation();
  const starMut = useToggleStarMutation();
  const moveMut = useMoveDiagramMutation();
  const patchMut = usePatchDiagramMutation();
  const iconMut = useUpdateDiagramIconMutation();
  const queryClient = useQueryClient();

  const [settingsDiagram, setSettingsDiagram] = useState<DiagramMetadata | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DiagramMetadata | null>(null);
  const [shareTarget, setShareTarget] = useState<DiagramMetadata | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const view = params.get('view') ?? 'all';
  const folderId = params.get('folder');

  const folderMap = useMemo(() => new Map(folders.map((f) => [f.id, f])), [folders]);

  const counts = useMemo(
    () => ({
      all: diagrams.length,
      starred: diagrams.filter((d) => d.isStarred).length,
      shared: diagrams.filter((d) => d.isPublic).length,
    }),
    [diagrams]
  );

  const rows = useMemo(() => {
    let list = [...diagrams];
    if (folderId) list = list.filter((d) => d.folderId === folderId);
    if (view === 'starred') list = list.filter((d) => d.isStarred);
    else if (view === 'shared') list = list.filter((d) => d.isPublic);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((d) => d.name.toLowerCase().includes(q));
    }
    list.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    if (view === 'recent') list = list.slice(0, 12);
    return list;
  }, [diagrams, folderId, view, search]);

  const open = (d: DiagramMetadata) => {
    const color = (d.folderId && folderMap.get(d.folderId)?.color) || null;
    openTab({ id: d.id, name: d.name, color, icon: d.icon, iconColor: d.iconColor });
    navigate(`/editor/${d.id}`);
  };

  const handleDuplicate = async (d: DiagramMetadata) => {
    setActionError(null);
    try {
      await duplicateMut.mutateAsync({ id: d.id });
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not duplicate diagram.');
    }
  };

  const handleMove = async (d: DiagramMetadata, targetFolderId: string | null) => {
    setActionError(null);
    try {
      await moveMut.mutateAsync({ diagramId: d.id, folderId: targetFolderId });
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not move diagram.');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setActionError(null);
    try {
      await deleteMut.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not delete diagram.');
    }
  };

  const setView = (v: string) => {
    const next = new URLSearchParams(params);
    next.delete('folder');
    if (v === 'all') next.delete('view');
    else next.set('view', v);
    setParams(next);
  };

  const TABS: { key: string; label: string; count?: number }[] = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'starred', label: 'Starred', count: counts.starred },
    { key: 'shared', label: 'Shared', count: counts.shared },
    { key: 'recent', label: 'Recent' },
  ];

  return (
    <div className="px-6 pb-16 pt-6 md:px-8">
      <div className="flex flex-col gap-[18px]">
        {/* Page header */}
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-[21px] font-semibold tracking-tight">
            {folderId ? folderMap.get(folderId)?.name ?? 'Diagrams' : 'Diagrams'}
          </h1>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger>
                <button className="ss-press inline-flex h-[34px] items-center gap-1.5 rounded-md border border-border bg-card px-3 text-[12.5px] font-medium shadow-sm transition-colors hover:bg-muted">
                  <Upload className="h-[15px] w-[15px]" /> Import <ChevronDown className="h-[13px] w-[13px] opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => importDiagramFromFile(navigate)}>
                  <Upload className="h-[15px] w-[15px]" /> JSON file
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => startMermaidImport(navigate)}>
                  <Workflow className="h-[15px] w-[15px]" /> Mermaid code
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button onClick={() => startAIGeneration(navigate)} className="ss-press inline-flex h-[34px] items-center gap-1.5 rounded-md border border-border bg-card px-3 text-[12.5px] font-medium shadow-sm transition-colors hover:bg-muted">
              <Sparkles className="h-[15px] w-[15px]" /> Generate with AI
            </button>
            <button onClick={() => navigate('/editor')} className="ss-press inline-flex h-[34px] items-center gap-1.5 rounded-md bg-foreground px-3 text-[12.5px] font-semibold text-background shadow-md transition-opacity hover:opacity-90">
              <Plus className="h-[15px] w-[15px]" /> Create diagram
            </button>
          </div>
        </div>

        <StarterTiles />

        {actionError && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-3.5 py-2.5 text-[12.5px] text-destructive">
            <span>{actionError}</span>
            <button
              onClick={() => setActionError(null)}
              className="ss-press font-semibold underline-offset-2 hover:underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Index */}
        <ContextMenu>
          <ContextMenuTrigger>
        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <div className="flex items-center gap-1 border-b border-border p-2.5">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setView(t.key)}
                className={`${idxTab} ${
                  view === t.key ? 'bg-muted font-semibold text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {t.label}
                {t.count !== undefined && <span className="font-mono text-[10.5px] text-muted-foreground">{t.count}</span>}
              </button>
            ))}
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <div className="flex h-[31px] w-[190px] items-center gap-2 rounded-md border border-transparent bg-muted px-2.5 text-[12.5px] text-muted-foreground focus-within:border-border focus-within:bg-card">
                <Search className="h-3.5 w-3.5" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onContextMenu={(e) => e.stopPropagation()}
                  placeholder="Filter diagrams"
                  className="w-full bg-transparent text-foreground outline-none placeholder:text-muted-foreground"
                />
              </div>
              <button className="ss-press inline-flex h-[31px] items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-[12.5px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                <ArrowUpDown className="h-3.5 w-3.5" /> Sort
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border text-left text-[11px] font-semibold text-muted-foreground">
                  <th className="px-3.5 py-2.5">Diagram</th>
                  <th className="px-3.5 py-2.5">Folder</th>
                  <th className="px-3.5 py-2.5 text-right">Nodes</th>
                  <th className="px-3.5 py-2.5">Visibility</th>
                  <th className="px-3.5 py-2.5 text-right">Last updated</th>
                  <th className="w-[44px] px-3.5 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {isLoading &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td colSpan={6} className="px-3.5 py-3.5">
                        <div className="h-6 w-full animate-pulse rounded bg-muted" />
                      </td>
                    </tr>
                  ))}

                {!isLoading && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3.5 py-16 text-center">
                      <p className="text-[14px] font-semibold text-foreground">No diagrams here yet</p>
                      <p className="mt-1 text-[12.5px] text-muted-foreground">Create your first diagram to get started.</p>
                      <button onClick={() => navigate('/editor')} className="ss-press mt-4 inline-flex h-[34px] items-center gap-1.5 rounded-md bg-foreground px-3 text-[12.5px] font-semibold text-background shadow-md transition-opacity hover:opacity-90">
                        <Plus className="h-[15px] w-[15px]" /> Create diagram
                      </button>
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  rows.map((d) => {
                    const folder = d.folderId ? folderMap.get(d.folderId) : undefined;
                    const color = folder?.color || 'var(--muted-foreground)';
                    return (
                      <DiagramRowContextMenu
                        key={d.id}
                        diagram={d}
                        folders={folders}
                        onOpen={() => open(d)}
                        onRename={() => setSettingsDiagram(d)}
                        onDuplicate={() => handleDuplicate(d)}
                        onToggleStar={() => starMut.mutate({ id: d.id, isStarred: !d.isStarred })}
                        onMove={(fId) => handleMove(d, fId)}
                        onShare={() => setShareTarget(d)}
                        onDelete={() => setDeleteTarget(d)}
                      >
                      <tr
                        onClick={() => open(d)}
                        onContextMenu={(e) => e.stopPropagation()}
                        className="cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted/60"
                      >
                        <td className="px-3.5 py-2.5">
                          <div className="flex items-center gap-3">
                            <span className="flex-none" onClick={(e) => e.stopPropagation()}>
                              <DiagramIconPicker
                                icon={d.icon}
                                color={d.iconColor}
                                triggerClassName="h-[30px] w-[30px] border border-border"
                                onChange={(icon, color) => iconMut.mutate({ id: d.id, icon, iconColor: color })}
                              />
                            </span>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 text-[13.5px] font-semibold">
                                <span className="truncate">{d.name}</span>
                                {d.isStarred && <Star className="h-3 w-3 flex-none fill-amber-500 text-amber-500" />}
                              </div>
                              <div className="truncate font-mono text-[11px] text-muted-foreground">
                                {d.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3.5 py-2.5">
                          {folder ? (
                            <span className="inline-flex items-center gap-2 text-[12.5px]">
                              <span className="h-2 w-2 flex-none rounded-[3px]" style={{ background: color }} />
                              {folder.name}
                            </span>
                          ) : (
                            <span className="text-[12.5px] text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3.5 py-2.5 text-right font-mono text-[12px] text-muted-foreground">{d.nodeCount}</td>
                        <td className="px-3.5 py-2.5">
                          {d.isPublic ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-[3px] text-[11.5px] font-medium text-emerald-600 dark:text-emerald-400">
                              <span className="h-1.5 w-1.5 rounded-full bg-current" /> Shared
                            </span>
                          ) : (
                            <span className="inline-flex rounded-full bg-muted px-2.5 py-[3px] text-[11.5px] font-medium text-muted-foreground">Private</span>
                          )}
                        </td>
                        <td className="px-3.5 py-2.5 text-right text-[12px] text-muted-foreground">{relativeTime(d.updatedAt)}</td>
                        <td className="px-3.5 py-2.5 text-right">
                          <div className="flex justify-end">
                            <DiagramRowMenu
                              diagram={d}
                              folders={folders}
                              onOpen={() => open(d)}
                              onRename={() => setSettingsDiagram(d)}
                              onDuplicate={() => handleDuplicate(d)}
                              onToggleStar={() => starMut.mutate({ id: d.id, isStarred: !d.isStarred })}
                              onMove={(fId) => handleMove(d, fId)}
                              onShare={() => setShareTarget(d)}
                              onDelete={() => setDeleteTarget(d)}
                            />
                          </div>
                        </td>
                      </tr>
                      </DiagramRowContextMenu>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {!isLoading && rows.length > 0 && (
            <div className="border-t border-border px-3.5 py-2.5 text-[12px] text-muted-foreground">
              {rows.length} diagram{rows.length === 1 ? '' : 's'}
            </div>
          )}
        </div>
          </ContextMenuTrigger>
          <ContextMenuContent className="w-52">
            <ContextMenuItem onSelect={() => navigate('/editor')}>
              <Plus /> New diagram
            </ContextMenuItem>
            <ContextMenuItem onSelect={() => startAIGeneration(navigate)}>
              <Sparkles /> Generate with AI
            </ContextMenuItem>
            <ContextMenuItem onSelect={() => importDiagramFromFile(navigate)}>
              <Upload /> Import…
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>

        <DiagramSettingsModal
          isOpen={!!settingsDiagram}
          onClose={() => setSettingsDiagram(null)}
          diagram={settingsDiagram}
          folders={folders}
          onUpdate={(id, data) => patchMut.mutateAsync({ id, data })}
          onMoveToFolder={(diagramId, fId) => moveMut.mutateAsync({ diagramId, folderId: fId })}
        />
        <DeleteConfirmModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
          title="Delete diagram"
          message={deleteTarget ? `"${deleteTarget.name}" will be permanently deleted.` : ''}
          isDeleting={deleteMut.isPending}
        />
        {shareTarget && (
          <ShareModal
            isOpen={!!shareTarget}
            onClose={() => setShareTarget(null)}
            diagramId={shareTarget.id}
            diagramName={shareTarget.name}
            initialIsPublic={shareTarget.isPublic}
            initialShareToken={shareTarget.shareToken}
            onShareUpdate={() =>
              queryClient.invalidateQueries({ queryKey: queryKeys.diagrams.list() })
            }
          />
        )}
      </div>
    </div>
  );
}
